import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import MappingTestControls from './MappingTestControls.tsx';
import TemplateCodeEditor from './TemplateCodeEditor.tsx';
import { BUILTIN_TEMPLATE_VARIABLES, collectTemplateVariables } from './templateVariables.ts';
import type { OutputMapperTestingConfig, RecordType } from './types.ts';
import FieldsConfig from '../../../document/components/FieldsConfig.tsx';
import type { OutputMapperConfig } from '../OutputMapperEditor/types.ts';

interface FileNameSectionProps {
    config: OutputMapperConfig;
    onConfigChange: (config: OutputMapperConfig) => void;
    /** For the inline test: the owner, its record type and the shared test setup of the editor panel. */
    ownerId?: string;
    recordType: RecordType;
    testingConfig: OutputMapperTestingConfig;
}

type FileNamePreview = { name: string | null; error?: string | null } | null;

// identifiers that usually go into a file name: document numbers, the customer number, the document date
const COMMON_FILE_NAME_FIELD =
    /^(order|request|invoice|quote|document|delivery_note|order_confirmation)_number$|customer(__)?_?number$|^(order|request|invoice|document)_date$/;

/**
 * The output file's name: a small two-line editor with `/` for all target fields and tags below it for the usual
 * identifiers and the built-ins. Applies to every output type; empty keeps `<record id>.<extension>`.
 */
const FileNameSection = ({ config, onConfigChange, ownerId, recordType, testingConfig }: FileNameSectionProps) => {
    const { t } = useTranslation('integrations');
    const [preview, setPreview] = useState<FileNamePreview>(null);
    // a file name has no loops: all plain target fields in the "/" menu, the usual identifiers as tags below
    const targetFields = collectTemplateVariables(config.output_mapping ?? []).filter(
        (variable) => variable.kind === 'variable'
    );
    const commonFields = targetFields.filter((variable) => COMMON_FILE_NAME_FIELD.test(variable.value)).slice(0, 4);

    return (
        <FieldsConfig.Section
            title={t('outputMapper.settings.fileName.label', 'File name')}
            description={t('outputMapper.settings.fileName.hint', {
                defaultValue:
                    'e.g. ORDERS_{{ order_number }}_{{ workist.date }}.xml. Empty keeps <record id>.<extension>.',
                // the description shows Jinja braces, which are not i18next placeholders
                skipInterpolation: true,
            })}
        >
            <TemplateCodeEditor
                value={config.file_name_template ?? ''}
                onValueChange={(next) => onConfigChange({ ...config, file_name_template: next || null })}
                variables={[...targetFields, ...BUILTIN_TEMPLATE_VARIABLES]}
                paletteVariables={[...commonFields, ...BUILTIN_TEMPLATE_VARIABLES]}
                format="txt"
                height="3rem"
                singleLine
                compact
                placeholder={t('outputMapper.settings.fileName.placeholder', 'Default: <record id>.<extension>')}
            />
            <div className="flex flex-wrap items-center gap-3">
                <MappingTestControls
                    integrationId={ownerId}
                    recordType={recordType}
                    config={config}
                    testingConfig={testingConfig}
                    showResultModal={false}
                    runLabel={t('outputMapper.settings.fileName.test', 'Test file name')}
                    onResult={(result) => setPreview({ name: result.fileName ?? null, error: result.error })}
                />
            </div>
            {preview &&
                (preview.error ? (
                    <span className="text-xs text-content-danger-strong">{preview.error}</span>
                ) : (
                    <span className="text-sm text-content-medium">
                        {t('outputMapper.settings.fileName.result', 'Resulting file name:')}{' '}
                        <span className="font-mono text-content-strong">
                            {preview.name ??
                                t('outputMapper.settings.fileName.defaultUsed', '<record id>.<extension> (default)')}
                        </span>
                    </span>
                ))}
        </FieldsConfig.Section>
    );
};

export default FileNameSection;
